import { Injectable } from '@angular/core';
import { ErrorHandlerService } from '@examdojo/error-handling';
import { mapToVoid } from '@examdojo/rxjs';
import { TableInsertModel, TableUpdateModel } from '@examdojo/supabase';
import { sanitizeObject } from '@examdojo/util/sanitize-object';
import { catchError, Observable, of, switchMap, tap, throwError } from 'rxjs';
import { ChallengeActivityHttpService } from './challenge-activity-http.service';
import { ChallengeActivityStatusHttpService } from './challenge-activity-status-http.service';
import {
  CHALLENGE_ACTIVITY_CREATE_ALLOWED_KEYS,
  CHALLENGE_ACTIVITY_UPDATE_ALLOWED_KEYS,
  ChallengeActivityCreateModel,
  ChallengeActivityHttpModel,
  ChallengeActivityStoreModel,
  ChallengeActivityUpdateModel,
} from './challenge-activity.model';
import { ChallengeActivityStore } from './challenge-activity.store';

@Injectable({ providedIn: 'root' })
export class ChallengeActivityService {
  constructor(
    private readonly challengeActivityHttpService: ChallengeActivityHttpService,
    private readonly challengeActivityStatusHttpService: ChallengeActivityStatusHttpService,
    private readonly errorHandler: ErrorHandlerService,
    private readonly store: ChallengeActivityStore,
  ) {}

  resetStore() {
    this.store.reset();
  }

  fetchAll() {
    return this.challengeActivityHttpService.fetchAll().pipe(
      tap((activities) => {
        this.store.set(activities);
      }),
      this.errorHandler.setHttpErrorMetadata({
        entity: 'examdojo.entity.challenge_activity',
      }),
    );
  }

  fetch(id: string): Observable<void> {
    return this.challengeActivityHttpService.fetch(id).pipe(
      tap((activity) => {
        this.store.upsert(activity.id, activity);
      }),
      this.errorHandler.setHttpErrorMetadata({
        entity: 'examdojo.entity.challenge_activity',
      }),
      mapToVoid(),
    );
  }

  startChallenge(assignmentSectionId: string): Observable<void> {
    return this.challengeActivityHttpService.startChallenge(assignmentSectionId).pipe(
      switchMap((challengeActivityId: string | null) => {
        if (!challengeActivityId) {
          return of(null);
        }

        return this.fetch(challengeActivityId);
      }),
      this.errorHandler.setHttpErrorMetadata({
        entity: 'examdojo.entity.challenge_activity',
        action: 'create',
      }),
      mapToVoid(),
    );
  }

  create(activity: ChallengeActivityCreateModel): Observable<void> {
    const payload = this.mapCreateModelToHttpInsertModel(activity);

    return this.challengeActivityHttpService.create(payload).pipe(
      tap((data) => {
        this.store.upsert(data.id, this.mapHttpModelToStoreModel(data));
      }),
      mapToVoid(),
      this.errorHandler.setHttpErrorMetadata({
        entity: 'examdojo.entity.challenge_activity',
      }),
    );
  }

  updateStatus(challengeActivityId: string, status: ChallengeActivityHttpModel['status']): Observable<void> {
    return this.challengeActivityStatusHttpService
      .create({
        challenge_activity_id: challengeActivityId,
        status,
      })
      .pipe(
        switchMap(() => {
          if (['COMPLETED', 'DISCARDED', 'FAILED'].includes(status)) {
            this.store.update(challengeActivityId, { status });
            return of(undefined);
          }

          return this.fetch(challengeActivityId);
        }),
        this.errorHandler.setHttpErrorMetadata({
          entity: 'examdojo.entity.challenge_activity',
          action: 'update',
        }),
        mapToVoid(),
      );
  }

  completeChallengeActivityWithStatus(
    challengeActivityId: string,
    status: ChallengeActivityHttpModel['status'] = 'COMPLETED',
  ) {
    return this.updateStatus(challengeActivityId, status).pipe(
      this.errorHandler.setHttpErrorMetadata({
        entity: 'examdojo.entity.challenge_activity',
        action: 'update',
      }),
      catchError((error: unknown) => {
        this.resetStore();
        return throwError(() => error);
      }),
    );
  }

  private mapCreateModelToHttpInsertModel(
    createModel: ChallengeActivityCreateModel,
  ): TableInsertModel<'challenge_activities', 'learning'> {
    return sanitizeObject(createModel, CHALLENGE_ACTIVITY_CREATE_ALLOWED_KEYS);
  }

  private mapUpdateModelToHttpUpdateModel(
    updateModel: ChallengeActivityUpdateModel,
  ): TableUpdateModel<'challenge_activities', 'learning'> {
    return sanitizeObject(updateModel, CHALLENGE_ACTIVITY_UPDATE_ALLOWED_KEYS);
  }

  private mapHttpModelToStoreModel(httpModel: ChallengeActivityHttpModel): ChallengeActivityStoreModel {
    return httpModel;
  }
}
